import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DinoId } from '../data/dinos';

export type CommsCharacter = 'mika' | 'bosco' | 'consequence';

export interface CommsLine {
  speaker: CommsCharacter;
  text: string;
  /** Optional override duration (ms). Otherwise auto-sized to text length. */
  durationMs?: number;
}

const PORTRAIT_SIZE = 44;
// Bubble width bumped from 200 to 220 so "MR CONSEQUENCE" has clearance
// without sitting flush against the right border, and height from 52 to
// 58 so the name label has breathing room from the top stroke (was at
// y=3 with fontSize 8 — visually kissed the bubble's 2px stroke).
const BUBBLE_W = 220;
const BUBBLE_H = 58;
const BOX_PAD = 3;
const TYPE_INTERVAL = 28;
/** Default y — top-right under the HUD row. */
const COMMS_Y_DEFAULT = 44;

/**
 * Star Fox 64-style radio comms overlay. A portrait + speech bubble slides
 * in from off-screen right, the text types out one character at a time, then
 * the box slides off after a duration. Multiple calls queue.
 *
 * Each scene owns its own CommsBox instance and calls .say(...) at events.
 */
export class CommsBox {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private bubbleBg: Phaser.GameObjects.Rectangle;
  private bubbleStroke: Phaser.GameObjects.Rectangle;
  private pBox!: Phaser.GameObjects.Rectangle;
  private portrait: Phaser.GameObjects.Image;
  private nameLabel: Phaser.GameObjects.Text;
  private text: Phaser.GameObjects.Text;
  private queue: CommsLine[] = [];
  private busy = false;
  private typeTimer: ReturnType<typeof setInterval> | null = null;
  /** Pending Phaser timer for slide-out — tracked so sayStatic can cancel
   *  any in-flight dismissal that would yank the static comms off screen
   *  when the scene resumes after a pause. */
  private slideOutTimer: Phaser.Time.TimerEvent | null = null;
  /** Set true when the owning scene shuts down. All async callbacks
   *  (setInterval typing, tween onComplete handlers) check this flag and
   *  bail before touching any GameObjects, since those are destroyed with
   *  the scene. Without this, mid-sequence scene transitions crash with
   *  "Cannot read properties of null/undefined" inside Phaser internals. */
  private disposed = false;

  /**
   * @param scene - parent scene
   * @param y     - vertical position. Default top-right under the HUD;
   *                Title scene passes a bottom value so the comms doesn't
   *                cover the central title text.
   */
  constructor(scene: Phaser.Scene, y: number = COMMS_Y_DEFAULT) {
    this.scene = scene;

    const offX = GAME_WIDTH + 10;
    // Depth 950 lifts the comms ABOVE the HUD (which uses 900 for the
    // score / [SND] / [GOD] indicators top-right). Without this, the
    // HUD text drew on top of the comms name label and clipped "MR
    // CONSEQUENCE" / "BOSCO" wherever they crossed the indicators.
    this.container = scene.add
      .container(offX, y)
      .setScrollFactor(0)
      .setDepth(950);

    // Portrait box matches bubble HEIGHT so the two render flush at top
    // and bottom (the bubble grew to 58 for name-label breathing room;
    // portrait used to be 50 tall and looked stunted next to it). The
    // portrait IMAGE itself stays 44×44 — we just center it in the
    // taller box.
    const pBoxSize = BUBBLE_H;
    const portraitInset = (pBoxSize - PORTRAIT_SIZE) / 2;
    this.pBox = scene.add
      .rectangle(0, 0, pBoxSize, pBoxSize, 0x0d0a1a, 0.95)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(0, 0);
    const initialTex = scene.textures.exists('comms-mika') ? 'comms-mika' : 'star';
    this.portrait = scene.add
      .image(portraitInset, portraitInset, initialTex)
      .setOrigin(0, 0)
      .setDisplaySize(PORTRAIT_SIZE, PORTRAIT_SIZE);

    const bubbleX = pBoxSize + 6;
    this.bubbleBg = scene.add
      .rectangle(bubbleX, 0, BUBBLE_W, BUBBLE_H, 0x0d0a1a, 0.85)
      .setOrigin(0, 0);
    this.bubbleStroke = scene.add
      .rectangle(bubbleX, 0, BUBBLE_W, BUBBLE_H)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(0, 0);

    // Name label — pushed down to y=7 (was 3) so the top of the glyphs
    // sits well clear of the bubble's 2px stroke. Font bumped to 9px so
    // it actually reads.
    this.nameLabel = scene.add
      .text(bubbleX + 6, 7, 'MIKA', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '9px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    this.text = scene.add
      .text(bubbleX + 6, 22, '', {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffffff',
        wordWrap: { width: BUBBLE_W - 12 },
        lineSpacing: 1,
      })
      .setOrigin(0, 0);

    this.container.add([this.pBox, this.portrait, this.bubbleBg, this.bubbleStroke, this.nameLabel, this.text]);
    this.container.setVisible(false);

    // Auto-clean on scene shutdown / destroy so async callbacks (setInterval
    // typing, tween onComplete) don't fire against destroyed GameObjects.
    // Every scene that uses CommsBox gets this for free — no need to
    // remember to call shutdown() manually.
    scene.events.once('shutdown', () => this.shutdown());
    scene.events.once('destroy', () => this.shutdown());
  }

  /** True when the queue is empty AND no line is currently animating. Used
   *  by HandoffScene to wait until all comms have finished before the
   *  scene transitions to the next planet. */
  get isIdle(): boolean {
    return !this.busy && this.queue.length === 0;
  }

  say(line: CommsLine) {
    if (this.disposed) return;
    this.queue.push(line);
    if (!this.busy) this.next();
  }

  saySequence(lines: CommsLine[]) {
    if (this.disposed) return;
    for (const l of lines) this.queue.push(l);
    if (!this.busy) this.next();
  }

  /**
   * Show a comms line statically — instant, no slide-in tween, no typing
   * animation, no auto-dismiss timer. Used during scene pause where Phaser
   * tweens/timers don't run. Caller is responsible for hiding it (use
   * `hideStatic()`). Bypasses the normal queue.
   */
  sayStatic(line: CommsLine) {
    if (this.disposed) return;
    // Wipe any in-flight animated comms first — typing timer, pending
    // slide-out timer, and the queue.
    if (this.typeTimer) {
      clearInterval(this.typeTimer);
      this.typeTimer = null;
    }
    if (this.slideOutTimer) {
      this.slideOutTimer.remove(false);
      this.slideOutTimer = null;
    }
    this.queue = [];
    this.busy = false;

    this.applySpeakerStyle(line);
    this.text.setText(line.text);
    // Portrait box now matches BUBBLE_H so the two render the same height.
    const totalW = BUBBLE_H + 6 + BUBBLE_W;
    this.container.x = GAME_WIDTH - totalW - 4;
    this.container.setVisible(true);
  }

  /** Instantly hide the comms (no slide-out tween). Pair with sayStatic. */
  hideStatic() {
    if (this.disposed) return;
    this.container.setVisible(false);
    this.container.x = GAME_WIDTH + 10;
  }

  /** Resolve portrait, name label, and frame color for a given speaker. */
  private applySpeakerStyle(line: CommsLine) {
    if (this.disposed) return;
    const tex =
      line.speaker === 'bosco'
        ? 'comms-bosco'
        : line.speaker === 'consequence'
          ? 'comms-consequence'
          : 'comms-mika';
    if (this.scene.textures.exists(tex)) {
      this.portrait.setTexture(tex);
      this.portrait.setDisplaySize(PORTRAIT_SIZE, PORTRAIT_SIZE);
    }
    const displayName =
      line.speaker === 'bosco' ? 'BOSCO' : line.speaker === 'consequence' ? 'MR CONSEQUENCE' : 'MIKA';
    const labelColor =
      line.speaker === 'bosco' ? '#9ad8ff' : line.speaker === 'consequence' ? '#ff5a3a' : '#ffe23a';
    const frameColor = line.speaker === 'consequence' ? 0xff3a1a : 0xffffff;
    this.nameLabel.setText(displayName);
    this.nameLabel.setColor(labelColor);
    this.pBox.setStrokeStyle(2, frameColor);
    this.bubbleStroke.setStrokeStyle(2, frameColor);
  }

  private next() {
    if (this.disposed) return;
    const line = this.queue.shift();
    if (!line) {
      this.busy = false;
      return;
    }
    this.busy = true;

    // Mr Consequence gets a red frame so the player instantly reads
    // "uh oh, bad guy on comms" before they finish reading the line.
    this.applySpeakerStyle(line);
    this.text.setText('');
    this.container.setVisible(true);

    // Portrait box now matches BUBBLE_H so the two render the same height.
    const totalW = BUBBLE_H + 6 + BUBBLE_W;
    const targetX = GAME_WIDTH - totalW - 4;
    const startX = GAME_WIDTH + 10;
    this.container.x = startX;

    this.scene.tweens.add({
      targets: this.container,
      x: targetX,
      duration: 240,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        if (this.disposed) return;
        this.startTyping(line);
      },
    });
  }

  private startTyping(line: CommsLine) {
    if (this.disposed) return;
    let i = 0;
    if (this.typeTimer) clearInterval(this.typeTimer);
    this.typeTimer = setInterval(() => {
      // Bail if scene transitioned mid-typing — Text object would be
      // destroyed and setText would crash inside Phaser.
      if (this.disposed) {
        if (this.typeTimer) {
          clearInterval(this.typeTimer);
          this.typeTimer = null;
        }
        return;
      }
      if (i < line.text.length) {
        this.text.setText(line.text.slice(0, i + 1));
        i += 1;
      } else {
        if (this.typeTimer) {
          clearInterval(this.typeTimer);
          this.typeTimer = null;
        }
        const hold = line.durationMs ?? Math.max(900, line.text.length * 40);
        this.slideOutTimer = this.scene.time.delayedCall(hold, () => {
          this.slideOutTimer = null;
          if (this.disposed) return;
          this.slideOut();
        });
      }
    }, TYPE_INTERVAL);
  }

  private slideOut() {
    if (this.disposed) return;
    this.scene.tweens.add({
      targets: this.container,
      x: GAME_WIDTH + 10,
      duration: 220,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        if (this.disposed) return;
        this.container.setVisible(false);
        this.next();
      },
    });
  }

  shutdown() {
    // Idempotent — safe to call from both 'shutdown' and 'destroy' events.
    if (this.disposed) return;
    this.disposed = true;
    if (this.typeTimer) {
      clearInterval(this.typeTimer);
      this.typeTimer = null;
    }
    if (this.slideOutTimer) {
      this.slideOutTimer.remove(false);
      this.slideOutTimer = null;
    }
    this.queue = [];
    this.busy = false;
    // Container may already be destroyed by the scene; guard so we don't
    // crash on the unlucky ordering where shutdown runs after destroy.
    if (this.container && this.container.scene) {
      this.container.setVisible(false);
    }
  }
}

/**
 * Shuffle deck — picks each item once before re-shuffling. Used so comms
 * lines don't repeat within a single run, but still cycle eventually.
 *
 * Per-array state lives on the deck itself; resetting all decks happens at
 * scene start by calling resetCommsDecks().
 */
class Deck<T> {
  private order: number[] = [];
  private cursor = 0;
  constructor(private items: T[]) {
    this.reshuffle();
  }
  draw(): T {
    if (this.cursor >= this.order.length) this.reshuffle();
    const idx = this.order[this.cursor++];
    return this.items[idx];
  }
  private reshuffle() {
    this.order = this.items.map((_, i) => i);
    // Fisher–Yates
    for (let i = this.order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.order[i], this.order[j]] = [this.order[j], this.order[i]];
    }
    this.cursor = 0;
  }
  reset() {
    this.reshuffle();
  }
}

// --- Line catalog -------------------------------------------------------
//
// Each list aims for 20+ entries so a normal run almost never repeats and
// it takes many full plays to see all of them. Mika is energetic +
// supportive radio dispatcher. Bosco is a posh whippet — dry humor, treats
// himself like the team's wise elder, food-obsessed.

const m = (text: string): CommsLine => ({ speaker: 'mika', text });
const b = (text: string): CommsLine => ({ speaker: 'bosco', text });
/** Mr Consequence — the villain. Sneers when his bosses are about to fight,
 *  and seethes when they lose. Cartoon-villain register: smug, theatrical,
 *  a bit of a drama queen, never quite mean enough to scare a kid. */
const v = (text: string): CommsLine => ({ speaker: 'consequence', text });

const BOSS_INCOMING_LINES: CommsLine[] = [
  m('Big one inbound! Watch the patterns!'),
  m('Hostile detected — heavy reading on the scope.'),
  m('Boss class incoming — high mass signature!'),
  m('Code red! Massive enemy on radar!'),
  m('Bracing position — the big one is here.'),
  m('Heads up — this thing is NOT happy.'),
  m('Battle stations! Battle stations!'),
  m('Inbound hostile — pattern unknown!'),
  m('Big shadow on the deck — eyes up!'),
  m('Look alive down there!'),
  m('Confirmed kill priority — engage!'),
  m('Telemetry is screaming. So am I.'),
  m('Brace, brace, brace!'),
  m('I have eyes on a very rude individual.'),
  m('Get your guns up! And your boots!'),
  b('Captain, this one is rather larger than the last.'),
  b('I would suggest aiming for the head.'),
  b('Best of luck, captain.'),
  b('I shall observe from a safe distance. Good luck.'),
  b('Stomping above will yield greater dividends.'),
  b('Pip pip, captain. Tally ho.'),
  b('Concentrate fire, captain. Concentrate.'),
  b('I shall bark menacingly from the bridge.'),
  b('How dreadfully large. Carry on.'),
  b('Do try not to die. The carpet is freshly cleaned.'),
  b('A spot of bother appears to be approaching.'),
  b('I once defeated three squirrels at once. Take inspiration.'),
  b('I admire your restraint in not running away.'),
  b('This shan\'t take long, surely?'),
  b('Pardon the interruption — large fellow incoming.'),
];

const BOSS_DEFEATED_LINES: CommsLine[] = [
  m('BOOM! That is how it is done!'),
  m('Beautiful work. Onward!'),
  m('Boss down! Phenomenal stomping.'),
  m('TARGET ELIMINATED! Hoo!'),
  m('Crater central! Nice!'),
  m('Mission accomplished — moving on!'),
  m('Read that telemetry — perfection.'),
  m('Oh that was BRUTAL. I love it.'),
  m('Top marks across the board!'),
  m('Smoke them if you got them — boss is DOWN.'),
  m('Did you hear me cheering? I was cheering.'),
  m('I am SCREAMING at the radar!'),
  m('That is going on the highlight reel!'),
  m('Whoever doubted you, send me their address.'),
  m('Blaze of glory mode confirmed!'),
  b('Splendid. Truly splendid work.'),
  b('Marvelous, captain. Quite marvelous.'),
  b('A flawless display of bipedal dominance.'),
  b('Bravo. Genuinely, bravo.'),
  b('I shall write a sonnet about that.'),
  b('Most satisfying. I felt it in my whiskers.'),
  b('A treat is on order. For me, I mean.'),
  b('Pip pip! Tally ho!'),
  b('First class form. As expected.'),
  b('Mother shall hear of this triumph.'),
  b('I have never felt so vicarious.'),
  b('I shall chase my tail in celebration.'),
  b('Most invigorating, captain.'),
  b('I am unsettled, but pleased.'),
  b('Oh, very nicely handled.'),
];

const ENCOURAGEMENT_LINES: CommsLine[] = [
  m('Looking good down there!'),
  m('Form is on point. Keep going!'),
  m('You are scoring like nobody is watching!'),
  m('I am taking notes for the highlight reel!'),
  m('Crowd is going wild up here!'),
  m('Keep that streak ALIVE!'),
  m('If I had popcorn I would be eating it.'),
  m('Are you SHOWING OFF? Because it is working.'),
  m('This is the run, isn\'t it. THIS is the run.'),
  m('I am calling your mum. She will be so proud.'),
  m('Smooth as butter from up here!'),
  m('That is your mission report writing itself!'),
  m('ICONIC stuff!'),
  m('We may need to upgrade your title.'),
  m('Witness me — I am witnessing!'),
  m('Stop being so good, you are making me cry.'),
  b('Steady hand, captain. Steady hand.'),
  b('Watching from the bridge. Proud of you.'),
  b('A modest treat would be appreciated post-mission.'),
  b('I would help, but I have no thumbs.'),
  b('Splendid form! Truly!'),
  b('I would chase, were there sticks.'),
  b('I would write this down, but, you know, paws.'),
  b('I have eaten three biscuits in your honour.'),
  b('My tail is wagging. That counts as cheering.'),
  b('I shall howl in support shortly.'),
  b('Maintaining keen observational duty.'),
  b('I support you with great whippet enthusiasm.'),
  b('If you see a squirrel, do let me know.'),
  b('Mother would be proud.'),
  b('You make this look effortless, captain.'),
  b('I have prepared a small celebratory bark.'),
];

const START_LINES: Record<DinoId, CommsLine[]> = {
  doux: [
    m('Bob! Glacier ahead — watch the icicles!'),
    m('Bob, frostbites bite first. Move it!'),
    m('Bob! Cold morning for it. Get going!'),
    m('Bob, ice may crack underfoot. Mind the gap.'),
    m('Bob! Pretty up there. Now ruin it.'),
    b('Bob, do try to keep your tail off the ice.'),
    b('A glacier, captain. Quite refreshing.'),
  ],
  mort: [
    m('Roxy, full throttle. Lava pits everywhere — keep moving.'),
    m('Roxy! Fast and furious. You ready?'),
    m('Roxy, three hearts. Do better than that.'),
    m('Roxy! Burn it all down!'),
    m('Roxy, you reckless wonder. GO.'),
    b('Roxy, the floor is, regrettably, lava.'),
    b('Roxy. Do bring me a singed pebble. For science.'),
  ],
  tard: [
    m('Biscuit! Stomp those crabs into the dunes.'),
    m('Biscuit, slow and steady — but please, less slow.'),
    m('Biscuit, you have the HP. Use it.'),
    m('Biscuit! Big fella, big day.'),
    m('Biscuit, take your time. Just not too much time.'),
    b('Biscuit, mind the sand. It gets everywhere.'),
    b('Biscuit. The crabs look insolent. Address them.'),
  ],
  vita: [
    m('Sprout! Light feet, big air.'),
    m('Sprout, the heights are yours. Up you go.'),
    m('Sprout! Bouncy bouncy bouncy.'),
    m('Sprout, mind the void. Or do not. Free fall builds character.'),
    m('Sprout! Sproing factor max!'),
    b('Sprout, splendid altitude. Splendid.'),
    b('Sprout. The mushrooms are sentient and bouncy. Truly a planet.'),
  ],
};

const GAME_OVER_LINES: Record<DinoId, CommsLine[]> = {
  doux: [
    m('Bob down! Hold on, we are sending evac!'),
    m('Bob! Hold tight, recovery inbound! ...oh.'),
    b('Captain Bob is a snow angel now. Lovely, really.'),
  ],
  mort: [
    m('Roxy! Talk to me!  ...evac on the way.'),
    m('Roxy, we lost you. Hang in there.'),
    b('Roxy has joined the lava. She always wanted to.'),
  ],
  tard: [
    m('Biscuit, big guy, we got you. Hang on.'),
    m('Biscuit! Down! Send recovery!'),
    b('Biscuit is now part of the dunes. Geologically speaking.'),
  ],
  vita: [
    m('Sprout! Mayday mayday, evac inbound!'),
    m('Sprout went down — get the rescue rocket!'),
    b('Sprout is one with the canopy now. Quite poetic.'),
  ],
};

const LEVEL_CLEARED_LINES: Record<DinoId, CommsLine[]> = {
  doux: [
    m('Glacier is yours, Bob. Phenomenal.'),
    m('Bob clears the ice! Gold star!'),
    b('Bob. The glacier shall remember your name.'),
  ],
  mort: [
    m('Inferno cleared! Lava pits forgive nothing — well done.'),
    m('Roxy tames the fire! YES!'),
    b('Roxy. The lava bows to you. Briefly.'),
  ],
  tard: [
    m('Dunes cleared! Biscuit, you walking tank.'),
    m('Biscuit owns the desert! Salutations.'),
    b('Biscuit. The sand is now slightly afraid of you.'),
  ],
  vita: [
    m('Canopy is down! Sprout, the heights are yours.'),
    m('Sprout rules the canopy! UNREAL!'),
    b('Sprout. The mushrooms surrender. As they should.'),
  ],
};

const GAUNTLET_START: CommsLine[] = [
  m('Full crew on the gauntlet — go go go!'),
  m('All four planets — let us make history.'),
  m('Crew assembled. Hit it!'),
  b('Captain — quite the marathon ahead. Steady on.'),
];

/**
 * Mr Consequence — recurring villain who sent the bosses. Taunts before
 * each boss fight; seethes when his minions get pasted.
 *
 * Universal taunts (used as fallback) and per-planet taunts (for flavor).
 */
const VILLAIN_TAUNT_LINES: Record<DinoId | 'any', CommsLine[]> = {
  any: [
    v('Ah-ah-ah... my finest minion awaits!'),
    v('You will not pass this one, little reptile.'),
    v('Behold! My masterpiece of malice!'),
    v('Oh ho ho, this is going to be DELICIOUS.'),
    v('You DARE? After all I have built?'),
    v('Let us see how you handle THIS.'),
    v('My pet has been waiting just for you.'),
    v('Bow before my brilliance! ...optional but encouraged.'),
  ],
  doux: [
    v('Behold — my Frozen Comet! Slip and DIE!'),
    v('The glacier was MY canvas. You shall not finish it.'),
    v('Cold? Good. You will be colder.'),
  ],
  mort: [
    v('My Magma Orb hungers, little flame!'),
    v('Welcome to the kitchen. You are the snack.'),
    v('I built this lava just for YOU.'),
  ],
  tard: [
    v('Behold the Sand Worm — you should have stayed home.'),
    v('My deserts welcome you. They will also keep you.'),
    v('You crawl too slowly to escape MY worm.'),
  ],
  vita: [
    v('My Sky Hunter is FAR above your league!'),
    v('Up here, the sky belongs to ME.'),
    v('Catch me, little dino. If you can fly.'),
  ],
};

/**
 * Two-beat exchanges that play when a gauntlet run begins. Mr Consequence
 * sneers at the audacity of running all four planets, then either Mika or
 * Bosco fires back. Picked once at gauntlet start.
 */
const GAUNTLET_VILLAIN_INTRO: CommsLine[][] = [
  [
    v('All four planets? You will not last past the first!'),
    m('Watch us, Consequence. Watch us cook.'),
  ],
  [
    v('Ha! A full marathon? You will be jelly by Inferno.'),
    b('Captain prefers his jelly with toast, sir.'),
  ],
  [
    v('All four bosses are mine. They will not lose.'),
    m('Bold words. Bigger guns over here.'),
  ],
  [
    v('You think you can beat me? In one sitting?'),
    b('We have an excellent sitting record, actually.'),
  ],
  [
    v('Four planets. Four catastrophes. Four humiliations!'),
    m('Four wins inbound. Buckle up.'),
  ],
];

/** Mr Consequence on the planet-select screen — TEACHING the player how
 *  to score high. Smug villain tutor: every line communicates one of the
 *  three leaderboard rules — (a) only gauntlet runs post, (b) score is
 *  primary, (c) time is the tiebreaker. A kid hearing a few of these
 *  during their first plays will understand exactly what the leaderboard
 *  values. */
const VILLAIN_LEVELSELECT_LINES: CommsLine[] = [
  v('Listen: only gauntlet runs post to my leaderboard.'),
  v('Solo planets are practice. Only gauntlets count for the leaderboard.'),
  v('Want a high score? Run all four planets in one gauntlet.'),
  v('Single planets won\'t earn you a name on my leaderboard.'),
  v('My leaderboard demands a full gauntlet — all four planets, one run.'),
  v('Tip: leaderboard sorts by score. Faster times break ties.'),
  v('Score gets you ranked. A fast time breaks the tie. Both matter.'),
  v('Single levels? Pfft. My leaderboard is gauntlet runs only.'),
  v('Want a top spot? Beat my full gauntlet end-to-end without dying.'),
  v('Faster gauntlets rank higher when scores are tied. Run clean AND fast.'),
];

/** Mr Consequence taunts when the player hits pause — small still text, not
 *  comms-box (the scene is pausing). Keep these short for the overlay. */
const PAUSE_TAUNT_LINES: string[] = [
  'Taking a break? How adorable.',
  'Catching your breath? Smart.',
  'Gone for a snack? Bring me one.',
  'Pause all you want. I do not.',
  'Tick tock, little dino.',
  'Stalling will not save you.',
  'Even my minions take fewer breaks.',
  'I am still watching, you know.',
];

/** Mr Consequence taunts when the player quits the level early. */
const QUIT_TAUNT_LINES: string[] = [
  'Running away? I knew it!',
  'Yes! Flee, little dino, flee!',
  'See? Too much for you. Always was.',
  'Crying off home, are we?',
  'Cowardly and predictable. Marvelous.',
  'My bosses send their regards. And laughter.',
];

/**
 * Lines Mr Consequence delivers when one of his bosses is wrecked.
 * Per-planet seethes — used on the cleared screens. Always a setup for the
 * next encounter so the kid feels the rivalry building.
 */
const VILLAIN_DEFEATED_LINES: Record<DinoId, CommsLine[]> = {
  doux: [
    v('My beautiful comet! Curse you, Bob!'),
    v('No matter — the next planet is FAR worse...'),
    v('Bah! Beginners luck. I will be back.'),
  ],
  mort: [
    v('My Magma Orb! Roxy, you menace!'),
    v('You burned my best minion. UNFORGIVABLE.'),
    v('Enjoy this victory. The Dunes will not be kind.'),
  ],
  tard: [
    v('My worm! Biscuit, you DOUGHY brute!'),
    v('How can a slow dino be so... thorough?'),
    v('The Canopy will stop you. It MUST.'),
  ],
  vita: [
    v('NO! My Sky Hunter, my magnum opus!'),
    v('Sprout, you absolute pest!'),
    v('I will plot something even worse. JUST YOU WAIT.'),
  ],
};

const GAUNTLET_CLEARED: CommsLine[] = [
  m('ALL FOUR PLANETS CLEAR! Captain, you legend!'),
  m('We just rewrote the leaderboard!'),
  b('I am proud of you, captain. Truly.'),
];

// --- Decks --------------------------------------------------------------

const decks = {
  bossIncoming: new Deck(BOSS_INCOMING_LINES),
  bossDefeated: new Deck(BOSS_DEFEATED_LINES),
  encouragement: new Deck(ENCOURAGEMENT_LINES),
  start: {
    doux: new Deck(START_LINES.doux),
    mort: new Deck(START_LINES.mort),
    tard: new Deck(START_LINES.tard),
    vita: new Deck(START_LINES.vita),
  } as Record<DinoId, Deck<CommsLine>>,
  gameOver: {
    doux: new Deck(GAME_OVER_LINES.doux),
    mort: new Deck(GAME_OVER_LINES.mort),
    tard: new Deck(GAME_OVER_LINES.tard),
    vita: new Deck(GAME_OVER_LINES.vita),
  } as Record<DinoId, Deck<CommsLine>>,
  levelCleared: {
    doux: new Deck(LEVEL_CLEARED_LINES.doux),
    mort: new Deck(LEVEL_CLEARED_LINES.mort),
    tard: new Deck(LEVEL_CLEARED_LINES.tard),
    vita: new Deck(LEVEL_CLEARED_LINES.vita),
  } as Record<DinoId, Deck<CommsLine>>,
  gauntletStart: new Deck(GAUNTLET_START),
  gauntletCleared: new Deck(GAUNTLET_CLEARED),
  villainTaunt: {
    any: new Deck(VILLAIN_TAUNT_LINES.any),
    doux: new Deck(VILLAIN_TAUNT_LINES.doux),
    mort: new Deck(VILLAIN_TAUNT_LINES.mort),
    tard: new Deck(VILLAIN_TAUNT_LINES.tard),
    vita: new Deck(VILLAIN_TAUNT_LINES.vita),
  } as Record<DinoId | 'any', Deck<CommsLine>>,
  villainDefeated: {
    doux: new Deck(VILLAIN_DEFEATED_LINES.doux),
    mort: new Deck(VILLAIN_DEFEATED_LINES.mort),
    tard: new Deck(VILLAIN_DEFEATED_LINES.tard),
    vita: new Deck(VILLAIN_DEFEATED_LINES.vita),
  } as Record<DinoId, Deck<CommsLine>>,
  gauntletVillainIntro: new Deck(GAUNTLET_VILLAIN_INTRO),
  pauseTaunt: new Deck(PAUSE_TAUNT_LINES),
  quitTaunt: new Deck(QUIT_TAUNT_LINES),
  villainLevelSelect: new Deck(VILLAIN_LEVELSELECT_LINES),
};

/** Reset all decks — call at the start of each run so each playthrough is fresh. */
export function resetCommsDecks() {
  decks.bossIncoming.reset();
  decks.bossDefeated.reset();
  decks.encouragement.reset();
  for (const d of ['doux', 'mort', 'tard', 'vita'] as DinoId[]) {
    decks.start[d].reset();
    decks.gameOver[d].reset();
    decks.levelCleared[d].reset();
  }
  decks.gauntletStart.reset();
  decks.gauntletCleared.reset();
  for (const d of ['doux', 'mort', 'tard', 'vita'] as DinoId[]) {
    decks.villainTaunt[d].reset();
    decks.villainDefeated[d].reset();
  }
  decks.villainTaunt.any.reset();
  decks.gauntletVillainIntro.reset();
  decks.pauseTaunt.reset();
  decks.quitTaunt.reset();
  decks.villainLevelSelect.reset();
}

/** Pick a non-repeating line for the given event. */
export const COMMS = {
  bossIncoming: () => decks.bossIncoming.draw(),
  bossDefeated: () => decks.bossDefeated.draw(),
  encouragement: () => decks.encouragement.draw(),
  start: (id: DinoId) => decks.start[id].draw(),
  gameOver: (id: DinoId) => decks.gameOver[id].draw(),
  levelCleared: (id: DinoId) => decks.levelCleared[id].draw(),
  gauntletStart: () => decks.gauntletStart.draw(),
  gauntletCleared: () => decks.gauntletCleared.draw(),
  /** Mr Consequence's pre-boss taunt. 50/50 chance per-planet flavor vs
   *  a generic taunt, so the kid hears both kinds of lines. */
  villainTaunt: (id: DinoId) =>
    Math.random() < 0.55 ? decks.villainTaunt[id].draw() : decks.villainTaunt.any.draw(),
  villainDefeated: (id: DinoId) => decks.villainDefeated[id].draw(),
  /** Two-line gauntlet open: villain sneer + Mika/Bosco snap-back. */
  gauntletVillainIntro: () => decks.gauntletVillainIntro.draw(),
  /** Plain string for the PAUSED overlay subtitle (no comms box). */
  pauseTaunt: () => decks.pauseTaunt.draw(),
  /** Plain string for the quit-to-level-select transition. */
  quitTaunt: () => decks.quitTaunt.draw(),
  /** Mr Consequence chimes in on the planet-select screen, daring the
   *  player to attempt the gauntlet / chase the leaderboard. */
  villainLevelSelect: () => decks.villainLevelSelect.draw(),
};
